import type { Row, FilterFn } from "../Table.types";
import { filterFns as tanstackFilterFns } from "@tanstack/react-table";
import { FILTER_TYPES } from "../TableFilters/TableFilters.constants";

/**
 * Wraps a TanStack filter function to preserve expanded rows logic
 */
const wrapFilterFn = <T>(filterFn: FilterFn<T>): FilterFn<T> => {
  return (row: Row<T>, columnId: string, filterValue: any, ...args) => {
    // Always ignore sub rows from filtering
    if (row.depth > 0) return true;

    return filterFn(row, columnId, filterValue, ...args);
  };
};

// Custom numeric filter that handles type conversion
const numericFilter: FilterFn<any> = (row, columnId, filterValue) => {
  if (row.depth > 0) return true;

  const value = row.getValue(columnId);
  const numValue = Number(value);
  const numFilter = Number(filterValue);

  // Check if both values are valid numbers
  if (isNaN(numValue) || isNaN(numFilter)) return false;

  return numValue === numFilter;
};

// Map our filter types to TanStack's built-in filter functions
export const filterFunctions: Record<string, FilterFn<any>> = {
  [FILTER_TYPES.TEXT]: wrapFilterFn(tanstackFilterFns.includesString),
  [FILTER_TYPES.INTEGER]: wrapFilterFn(numericFilter),
  [FILTER_TYPES.DECIMAL]: wrapFilterFn(numericFilter),
  [FILTER_TYPES.DATE]: wrapFilterFn(tanstackFilterFns.equals),
  [FILTER_TYPES.DATE_RANGE]: wrapFilterFn(tanstackFilterFns.equals),
  [FILTER_TYPES.BOOLEAN]: wrapFilterFn(tanstackFilterFns.equals),
  [FILTER_TYPES.SELECT]: wrapFilterFn(tanstackFilterFns.arrIncludesSome)
};

// Default filter function (used when type is not specified)
/**
 * Custom filter function for table columns
 *
 * @template T - The type of data in the table
 * @param {Row<T>} row - The row to be filtered
 * @param {string} columnId - The ID of the column being filtered
 * @param {any} filterValue - The value to filter by
 * @returns {boolean} - True if the row passes the filter, false otherwise
 */
export const customFilterFn = <T>(
  row: Row<T>,
  columnId: string,
  filterValue: any
) => {
  // Ignore sub rows from filtering
  if (row.depth > 0) return true;

  const value = row.getValue(columnId);
  return String(value)
    .toLowerCase()
    .includes(String(filterValue).toLowerCase());
};
